<script lang="ts">
  import KBSectionNavItem from "./KBSectionNavItem.svelte";
  import { isNestedDocument, type WritableFolder } from "app/models/documents";
  import {
    type DocumentsStore,
    DragType,
    isDraggingAtTheRoot,
    isDragging,
  } from "app/stores/documents";
  import { canChangeSpaceGroup, canDeleteSpaceGroup } from "$stores/permissions";
  import { communitySlug } from "$stores/community";
  import cx from "classnames";
  import { dndzone, TRIGGERS } from "svelte-dnd-action";
  import { flip } from "svelte/animate";
  import { page } from "$app/stores";
  import KBNavItem from "$components/knowledge-base/KnowledgeBaseNavigation/KBNavItem.svelte";
  import { getMediaQuery } from "$utils/mediaQuery";
  import IconButtonTrigger from "$components/+design-system/Dropdown/fields/IconButtonTrigger.svelte";
  import Action from "$components/+design-system/Dropdown/fields/Action.svelte";
  import { Dots, Pencil, Trash } from "$components/+shared/OVIcon";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import KBFolder from "$components/knowledge-base/KnowledgeBaseNavigation/KBFolder.svelte";
  import {
    handleSharedDraggingBehaviour,
    isDndPlaceholder,
    transformDraggedElement,
  } from "./dndUtils";
  import DndPlaceholder from "$components/knowledge-base/KnowledgeBaseNavigation/DndPlaceholder.svelte";

  export let folder: WritableFolder;
  export let docs: DocumentsStore;
  export let isEditModalOpen: boolean = false;
  export let isDeleteModalOpen: boolean = false;

  let hasDraggedEntered: boolean;

  const ANIMATION_DURATION_MS = 110;
  const isDesktop = getMediaQuery("desktop");
  const { currentDragType } = docs;

  // we reset the hasDraggedEntered whenever the isDragging value changes
  $: {
    hasDraggedEntered = false;
    $isDragging;
  }

  async function sortDocs(e: CustomEvent) {
    docs.preventUpdates(); // prevent server updates from colliding with user

    handleSharedDraggingBehaviour(e);

    // if the drags and item inside the section, we set the hasDraggedEntered to true
    if (e.detail.info.trigger === TRIGGERS.DRAGGED_ENTERED) {
      hasDraggedEntered = true;
    }

    // if the user drags an item outside of the section, we set the drag value at the root state to true
    if (e.detail.info.trigger === TRIGGERS.DRAGGED_LEFT) {
      $isDraggingAtTheRoot = true;
    }

    if (e.detail.info.trigger === TRIGGERS.DRAG_STARTED) {
      const destination = $folder.items.findIndex((space) => space.uuid === e.detail.info.id);
      const folderItem = $folder.items[destination];

      // can be either "nested_folder" or "nested_document"
      $currentDragType =
        folderItem.folderItemType === "nested_folder"
          ? DragType.NestedFolder
          : DragType.NestedDocument;
    }

    // dropped outside of any zone, we don't need to update the order

    $folder.items = e.detail.items;
    // folder has been dropped into a zone, update the order in the backend
    if (e.detail.info.trigger === TRIGGERS.DROPPED_INTO_ZONE) {
      const destination = $folder.items.findIndex((space) => space.uuid === e.detail.info.id);
      const folderItem = $folder.items[destination];
      folderItem.moveAfterDragAndDrop(destination, $folder);
    }
  }

  let navItem: HTMLDivElement;
  let isDropdownOpen: boolean = false;
</script>

<div class="my-[22px]">
  <KBSectionNavItem name={$folder.title} sectionUuid={$folder.uuid}>
    <div
      class="min-h-0 rounded pb-0"
      use:dndzone={{
        items: $folder.items,
        flipDurationMs: ANIMATION_DURATION_MS,
        dragDisabled: !$canChangeSpaceGroup || !$isDesktop,
        type: "docs",
        dropTargetClasses: [
          "min-h-[16px]",
          "border",
          "border-dashed",
          "border-blue-400/75",
          "dark:border-blue-400/45",
          "transition-[min-height]",
          "ease-out",
          "delay-[25ms]",
          "duration-[400ms]",
          "mx-2",
          "transition-[padding]",
        ],
        dropTargetStyle: {
          paddingBottom: hasDraggedEntered ? "8px" : "0",
        },
        // Accepts nested folders or documents
        dropFromOthersDisabled: $currentDragType === DragType.Section,
        transformDraggedElement,
      }}
      on:consider={sortDocs}
      on:finalize={sortDocs}
    >
      {#if $folder.items.length === 0}
        <!--
        Important, when these nav dropzones are completely empty, there is a bug where dropzones don't open properly. It is
        not enough to set min height in dropTargetClasses.
        -->
        <div />
      {/if}

      {#each $folder.items as folderItem (folderItem.uuid)}
        <div animate:flip={{ duration: ANIMATION_DURATION_MS }}>
          {#if isDndPlaceholder(folderItem)}
            <DndPlaceholder />
          {:else}
            <div
              class={cx(
                "transition-colors duration-500 ease-out",
                $page.url.pathname.includes(folderItem.uuid)
                  ? "border-community-primary"
                  : "border-black/10 dark:border-white/10"
              )}
              bind:this={navItem}
            >
              {#if isNestedDocument(folderItem)}
                <KBNavItem
                  communitySlug={$communitySlug}
                  document={folderItem}
                  on:expand-group={() => {
                    setTimeout(() => {
                      navItem?.scrollIntoView({
                        block: "nearest",
                        behavior: "smooth",
                      });
                    }, ANIMATION_DURATION_MS * 2);
                  }}
                />
              {:else}
                <KBFolder
                  {docs}
                  folder={folderItem}
                  parentSection={folder}
                  bind:hasDraggedEnteredParent={hasDraggedEntered}
                />
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>

    <svelte:fragment slot="cta">
      {#if $canChangeSpaceGroup || $canDeleteSpaceGroup}
        <div class="group-hover:block" class:block={isDropdownOpen} class:hidden={!isDropdownOpen}>
          <Dropdown bind:open={isDropdownOpen}>
            <IconButtonTrigger
              size="small"
              slot="trigger"
              icon={Dots}
              a11yLabel={"More actions"}
              let:active
              {active}
            />
            {#if $canChangeSpaceGroup}
              <Action
                icon={Pencil}
                title="Edit section"
                on:click={() => (isEditModalOpen = true)}
              />
            {/if}
            {#if $canDeleteSpaceGroup}
              <Action
                icon={Trash}
                title="Delete section"
                danger
                on:click={() => (isDeleteModalOpen = true)}
              />
            {/if}
          </Dropdown>
        </div>
      {/if}
    </svelte:fragment>
  </KBSectionNavItem>
</div>
